import React, { useState } from "react"
import { Button, Card, CardBody, CardFooter, CardText, Col, Tooltip, Spinner } from "reactstrap"
import { CanalContato } from ".."
import { GoogleIcon } from "./GoogleIcon"
import api from "../../../../../../../services/api"
import { useEffect, useCallback } from "react"

interface CanalCardProps extends CanalContato {
	onDelete: (canalId: string | number) => void
	onChangePrivacy: (canalId: string | number, isVisible: boolean) => void
	isConversasSync: { whatsapp: boolean; instagram: boolean }
	isEmailSync: boolean
	googleLogin: () => void
	openWAModal: () => void
	baileysUrl: string | null
	onConnectCoexistence: () => void
	onConnectPlayneeConversas: (numero: string) => void
	identificador: string
	refreshKey?: number
}

export function CanalCard({
	canal,
	identificador,
	isEmailSync,
	onDelete,
	isConversasSync,
	openWAModal,
	baileysUrl,
	onConnectCoexistence,
	onConnectPlayneeConversas,
	refreshKey,
}: CanalCardProps) {
	const [tooltipOpenEmail, setTooltipOpenEmail] = useState(false)
	const [playneeStatus, setPlayneeStatus] = useState<"loading" | "connected" | "disconnected" | "error">("loading")

	const toggleEmail = () => setTooltipOpenEmail(!tooltipOpenEmail)

	const formatPhoneNumber = (phoneNumber: string) => {
		if (!phoneNumber) return ""
		const cleaned = phoneNumber.replace(/\D/g, "")
		const match = cleaned.match(/^(\d{2})(\d{2})(\d{5})(\d{4})$/)
		if (match) {
			return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`
		}
		return phoneNumber
	}

	const isWhatsapp = canal.id === 25
	const isInstagram = canal.id === 2
	const isGoogleMail = canal.id === 12

	const getMainIcon = () => {
		if (isWhatsapp) {
			return <i className={canal?.icon || "fa-brands fa-whatsapp"} style={{ fontSize: "2.2rem" }} />
		}
		if (isGoogleMail) {
			return <i className={canal?.icon} style={{ fontSize: "2.2rem" }} />
		}
		return <i className={canal?.icon || "fas fa-portrait"} style={{ fontSize: "2.2rem" }} />
	}

	const fetchPlayneeStatus = useCallback(async () => {
		if (!isWhatsapp || !identificador) return
		try {
			setPlayneeStatus("loading")
			const numLimpo = identificador.replace(/\D/g, "")
			const { data } = await api.get(`/marketing/me/playnee-conversas/status/${numLimpo}`)
			setPlayneeStatus(data?.connected ? "connected" : "disconnected")
		} catch {
			setPlayneeStatus("error")
		}
	}, [isWhatsapp, identificador])

	useEffect(() => {
		fetchPlayneeStatus()
	}, [fetchPlayneeStatus, refreshKey])

	const isConnected = playneeStatus === "connected"
	const isLoading = playneeStatus === "loading"

	// Remove espaços e caracteres não numéricos antes de enviar para a API
	const sanitizeNumero = (raw: string) => raw.replace(/\D/g, "")

	const statusColor = isConnected ? "#2dce89" : isLoading ? "#fb6340" : "#adb5bd"

	return (
		<Col xs={12} sm={6} md={4} lg={3} key={canal.id}>
			<Card
				className="position-relative shadow border-0"
				style={{
					background: canal?.color || "#f7f7f9",
					height: "400px",
					borderRadius: "16px",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Botão de remover */}
				<Button
					close
					onClick={() => onDelete(canal?.id)}
					style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}
				/>

				<CardBody
					className="d-flex flex-column align-items-center overflow-hidden justify-content-center text-center pt-5 pb-4"
					style={{ flex: 1 }}
				>
					{/* Ícone principal */}
					<div
						className="d-flex align-items-center justify-content-center mb-3"
						style={{
							width: "72px",
							height: "72px",
							borderRadius: "50%",
							background: "rgba(255,255,255,0.9)",
							boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
						}}
					>
						{getMainIcon()}
					</div>

					<CardText className="font-weight-bold mb-1" style={{ fontSize: "1.1rem" }}>
						{canal?.nome}
					</CardText>

					<CardText
						className="text-muted mb-0 p-2"
						style={{
							fontSize: "0.9rem",
							maxWidth: "100%",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{isWhatsapp ? formatPhoneNumber(identificador) : identificador || "--"}
					</CardText>
				</CardBody>

				{/* Rodapé Email Google */}
				{isGoogleMail && (
					<CardFooter className="bg-transparent border-0 text-center pb-4 d-flex justify-content-center">
						<>
							<div
								id={`emailSync-${canal.id}`}
								className="d-flex align-items-center justify-content-center"
								style={{
									background: "#ffffff",
									padding: "6px 10px",
									borderRadius: "10px",
									position: "relative",
									minWidth: "56px",
									opacity: isEmailSync ? 1 : 0.4,
								}}
							>
								<GoogleIcon />
								{isEmailSync && (
									<span
										style={{
											position: "absolute",
											top: "-4px",
											right: "-4px",
											fontSize: "0.7rem",
											color: "#28a745",
										}}
									>
										<i className="fas fa-check" />
									</span>
								)}
							</div>
							{isEmailSync && (
								<Tooltip isOpen={tooltipOpenEmail} target={`emailSync-${canal.id}`} toggle={toggleEmail}>
									Email Google sincronizado com o Playnee.
								</Tooltip>
							)}
						</>
					</CardFooter>
				)}

				{/* Faixa de conexão Playnee Conversas */}
				{isWhatsapp && (
					<div
						role="button"
						onClick={() => onConnectPlayneeConversas(sanitizeNumero(identificador))}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "14px 18px",
							background: isConnected ? "#075e54" : "#128c7e",
							cursor: "pointer",
							transition: "all 0.2s ease",
							flexShrink: 0,
						}}
						onMouseEnter={e => { e.currentTarget.style.background = isConnected ? "#054d44" : "#0e7a6e" }}
						onMouseLeave={e => { e.currentTarget.style.background = isConnected ? "#075e54" : "#128c7e" }}
					>
						<div className="d-flex align-items-center" style={{ gap: "12px" }}>
							<i
								className="fas fa-comments"
								style={{ fontSize: "1.3rem", color: "rgba(255,255,255,0.9)" }}
							/>
							<div>
								<div style={{
									fontSize: "0.6rem",
									color: "rgba(255,255,255,0.65)",
									textTransform: "uppercase",
									letterSpacing: "0.8px",
									fontWeight: 700,
									lineHeight: 1.2,
								}}>
									Playnee Conversas
								</div>
								<div style={{
									fontSize: "0.87rem",
									color: "#fff",
									fontWeight: 600,
									lineHeight: 1.3,
								}}>
									{isLoading ? "Verificando..." : isConnected ? "Conectado" : "Toque para Conectar"}
								</div>
							</div>
						</div>

						<i
							className={isConnected ? "fas fa-cog" : "fas fa-plug"}
							style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.75)" }}
						/>
					</div>
				)}
			</Card>
		</Col>
	)
}
